import {
  Server,
  ReceiptText,
  LayoutDashboard,
  LogOut,
  UserCog,
  ReceiptIndianRupee,
  BookText,
  HeartPulse,
  ChartPie,
  SquareArrowOutUpRight,
  Cpu,
  ChartNoAxesCombined,
} from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { resetUser, selectUser } from "../store/features/userSlice";
import { Button } from "../components/ui/button";
import { hasPermission, openInNewTab } from "../lib/utils";

const AuthLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const menuItems = [
    {
      menuPath: "/monitoring-dashboard",
      path: "/monitoring-dashboard/resources",
      permission: "monitoring_dashboard",
      label: "Monitoring Dashboard",
      icon: <LayoutDashboard className="h-4 w-4" />,
      isDev: true,
    },
    {
      path: "/capacity-dashboard",
      permission: "capacity_dashboard",
      label: "Capacity Dashboard",
      icon: <ChartPie className="h-4 w-4" />,
      isDev: true,
    },
    {
      path: "/billing-dashboard",
      permission: "billing_dashboard",
      label: "Billing & Utilization",
      icon: <ReceiptIndianRupee className="h-4 w-4" />,
      isDev: true,
    },
    {
      path: "/orders",
      permission: "orders",
      label: "Orders",
      icon: <ReceiptText className="h-4 w-4" />,
    },
    {
      path: "/compute-capacity",
      permission: "compute_capacity",
      label: "Compute Capacity",
      icon: <Server className="h-4 w-4" />,
    },
    {
      path: "/gpu-dashboard",
      permission: "gpu_dashboard",
      label: "GPU Dashboard",
      icon: <ChartNoAxesCombined className="h-4 w-4" />,
    },
    {
      path: "/gpu-management",
      permission: "gpu_management",
      label: "GPU Management",
      icon: <Cpu className="h-4 w-4" />,
    },
    {
      path: "/user-management",
      permission: "user_management",
      label: "User Management",
      icon: <UserCog className="h-4 w-4" />,
    },
    {
      path: "#",
      permission: "knowledge_management",
      label: "Knowledge Management",
      icon: <BookText className="h-4 w-4" />,
      isExternal: true,
      onClick: () => {
        openInNewTab("https://docs.aws.amazon.com");
      },
    },
    {
      path: "#",
      permission: "service_health_dashboard",
      label: "Service Health Dashboard",
      icon: <HeartPulse className="h-4 w-4" />,
      isExternal: true,
      onClick: () => {
        openInNewTab("https://health.aws.amazon.com/health/status");
      },
    },
  ];

  return (
    <div className="h-screen flex">
      {/* Sidebar */}
      <div className="w-64 bg-card border-r border-border">
        <div className="h-16 flex items-center justify-center border-b border-border">
          <img
            src="/images/cms-logo-horiz.png"
            className="h-8"
            alt="CMS Logo"
          />
        </div>
        <nav className="p-2 space-y-2">
          {menuItems.map(
            (item) =>
              hasPermission(user.role, item.permission) && (
                <button
                  key={item.path}
                  className={`h-10 py-2 px-4 inline-flex items-center justify-between rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none w-full justify-start ${
                    location.pathname.includes(item?.menuPath ?? item.path)
                      ? "bg-black text-white"
                      : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                  onClick={item.onClick || (() => navigate(item.path))}
                >
                  <div className="flex items-center">
                    <span className="mr-2">{item.icon}</span>
                    {item.label}
                  </div>
                  {item?.isDev && (
                    <span className="ml-2 text-[8px] font-medium bg-yellow-300 text-black px-[2px] rounded leading-[12px]">
                      DEV
                    </span>
                  )}
                  {item?.isExternal && (
                    <span className="ml-2">
                      <SquareArrowOutUpRight className="w-3 text-gray-600" />
                    </span>
                  )}
                </button>
              ),
          )}
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col bg-background">
        <header className="sticky top-0 h-16 bg-card border-b border-border px-4 flex items-center justify-between">
          <div className="text-lg font-bold">Cloud Admin Portal</div>
          <div className="flex items-center space-x-4">
            <UserDropdown />
          </div>
        </header>
        <main className="flex-1 p-4 h-[calc(100vh_-_64px)] overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const UserDropdown = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const handleLogout = () => {
    dispatch(resetUser());
    navigate("/login");
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  return (
    <div className="relative flex items-center divide-x">
      <div className="h-10 py-2 px-4 inline-flex items-center rounded-md text-sm transition-colors disabled:opacity-50 disabled:pointer-events-none gap-2">
        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
          {getInitials(user?.firstName, user?.lastName)}
        </div>
        <span>{user?.name || "User"}</span>
      </div>
      <div>
        <Button variant="ghost" size="icon" className="rounded-full ml-2">
          <LogOut className="h-4 w-4" onClick={handleLogout} />
        </Button>
      </div>
    </div>
  );
};

export default AuthLayout;
